import {
  useEffect,
  useState,
  useRef,
  type ChangeEvent,
  type MutableRefObject,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { IoMdArrowBack } from 'react-icons/io';
import { MdOutlineFileUpload, MdClose } from 'react-icons/md';
import Webcam from 'react-webcam';
import { BiSave } from 'react-icons/bi';

import PlaceholderImage from '../../assests/placeholder.jpg';
import statesAndCities from '../../states_cities.json';
import { fetchTenant, updateTenant } from '../../redux/tenantActions';
import type { RootState, AppDispatch } from '../../redux/store';
import { showErrorToast } from '../../utils/toast';

interface TenantFormData {
  storeCoordinator: string;
  phone: string;
  address: string;
  tenantBrand: string;
  wing: string;
  floor: string;
}

type TenantFormErrors = Partial<
  TenantFormData & {
    state: string;
    city: string;
  }
>;

function TenantEditPage() {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const { tenant_details, loading } = useSelector((state: RootState) => state.tenant);
  const [formData, setFormData] = useState<TenantFormData>({
    storeCoordinator: '',
    phone: '',
    address: '',
    tenantBrand: '',
    wing: '',
    floor: '',
  });
  const [formErrors, setFormErrors] = useState<TenantFormErrors>({});
  const [image, setImage] = useState<string>(PlaceholderImage);
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [capturedFile, setCapturedFile] = useState<File | null>(null);
  const [, setLocationError] = useState(false);
  const [cameraError, setCameraError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const webcamRef = useRef<Webcam | null>(null);
  const [logo, setLogo] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const [selectedState, setSelectedState] = useState<string>(
    tenant_details?.state ?? '',
  );
  const [city, setCity] = useState<string>(tenant_details?.city ?? '');

  const openCameraModal = async () => {
    await requestCameraPermission();
    setShowModal(true);
  };
  const closeCameraModal = () => setShowModal(false);

  const requestLocationPermission = () => {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        setLatitude(lat);
        setLongitude(lng);
        setLocationError(false);
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('Error getting location: ', error);
        showErrorToast('Unable to access location. You can still continue without it.');
        setLocationError(true);
      },
    );
  };

  const requestCameraPermission = async () => {
    try {
      const constraints: MediaStreamConstraints = {
        video: {
          facingMode: { exact: 'environment' },
        },
      };
      await navigator.mediaDevices.getUserMedia(constraints);
      setCameraError(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error accessing camera: ', err);
      showErrorToast('Camera access denied. Enable permissions to capture store image.');
      setCameraError(true);
    }
  };

  const capture = async () => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      if (!imageSrc) return;
      setImage(imageSrc);
      const blob = await fetch(imageSrc).then((res) => res.blob());
      const fileFromBlob = new File([blob], 'captured_image.jpg', { type: blob.type });
      setCapturedFile(fileFromBlob);
      setShowModal(false);
    }
  };

  const handleLogoUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      setLogo(f);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result as string);
      };
      reader.readAsDataURL(f);
    }
  };

  const removeLogo = () => {
    setLogo(null);
    setLogoPreview(null);
  };

  const validateForm = (): TenantFormErrors => {
    const errors: TenantFormErrors = {};
    if (!formData.storeCoordinator.trim()) {
      errors.storeCoordinator = 'Contact Person Name is required.';
    }
    if (!formData.phone.trim()) {
      errors.phone = 'Contact Person Number is required.';
    } else if (!/^(?:\+91|91|0)?[6789]\d{9}$/.test(formData.phone)) {
      errors.phone = 'Phone number must be numeric with 10 digits.';
    }
    if (!formData.address.trim()) {
      errors.address = 'Address is required.';
    }
    if (!selectedState) {
      errors.state = 'State is required.';
    }
    if (!city) {
      errors.city = 'City is required.';
    }
    if (!formData.tenantBrand.trim()) {
      errors.tenantBrand = 'Brand Name is required.';
    }
    if (!formData.wing.trim()) {
      errors.wing = 'Wing is required.';
    }
    if (!formData.floor.trim()) {
      errors.floor = 'Floor is required.';
    }
    return errors;
  };

  const saveTenant = async () => {
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      showErrorToast('Please fix form errors before saving.');
      return;
    }
    try {
      await dispatch(
        updateTenant(
          formData.storeCoordinator,
          formData.phone,
          formData.address,
          formData.wing,
          formData.floor,
          formData.tenantBrand,
          capturedFile,
          logo,
          id,
          latitude,
          longitude,
          selectedState,
          city,
        ),
      );
      navigate(`/tenant/${id}`, { state: { tenant_details } });
    } catch {
      // Error toast handled in tenantActions.updateTenant
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(fetchTenant(id));
      requestLocationPermission();
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (tenant_details) {
      setFormData({
        storeCoordinator: tenant_details.storeCoordinator || '',
        phone: tenant_details.phone || '',
        address: tenant_details.address || '',
        tenantBrand: tenant_details.tenantBrand || '',
        wing: tenant_details.wing || '',
        floor: tenant_details.floor || '',
      });
      if (tenant_details.storeImage) {
        setImage(`${tenant_details.storeImage}?t=${new Date().getTime()}`);
      }
      setSelectedState(tenant_details.state || '');
      setCity(tenant_details.city || '');
    }
  }, [tenant_details]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { id: fieldId, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
    setFormErrors((prev) => ({
      ...prev,
      [fieldId]: '',
    }));
  };

  const modalContent = () => (
    <div className="p-4">
      {cameraError ? (
        <div className="text-red-600 mb-4">
          Camera access denied. Please enable camera access to capture image.
        </div>
      ) : (
        <div className="space-y-4">
          <Webcam
            audio={false}
            ref={webcamRef as MutableRefObject<Webcam | null>}
            screenshotFormat="image/jpeg"
            className="w-full rounded-lg"
            videoConstraints={{
              facingMode: { exact: 'environment' },
            }}
          />
          <button
            type="button"
            onClick={capture}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-brand text-white rounded-lg hover:bg-brand-dark transition-colors"
          >
            Capture Photo
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="h-screen bg-white overflow-auto">
      {loading && (
        <div className="fixed inset-0 bg-white/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {tenant_details && (
        <div className="h-full flex flex-col">
          <div className="bg-white border-b shadow-sm sticky top-0 z-30">
            <div className="max-w-[1200px] mx-auto">
              <div className="px-4 sm:px-6">
                <div className="h-16 flex items-center">
                  <button
                    type="button"
                    onClick={() => navigate(`/tenant/${id}`)}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <IoMdArrowBack className="w-5 h-5 text-gray-600" />
                  </button>
                  <div className="flex-1 flex justify-center">
                    <div className="text-center">
                      <h1 className="text-lg font-semibold text-gray-900">Edit Tenant</h1>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={saveTenant}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <BiSave className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 bg-gray-50">
            <div className="max-w-5xl mx-auto p-4 sm:p-6">
              <div className="space-y-6">
                <div className="relative">
                  <img
                    src={image}
                    alt="Store"
                    className="w-full h-64 object-cover rounded-lg shadow-sm"
                  />
                  <button
                    type="button"
                    onClick={openCameraModal}
                    className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/50 transition-colors rounded-lg"
                  >
                    <MdOutlineFileUpload className="w-8 h-8 text-white" />
                  </button>
                </div>

                <div className="bg-white rounded-lg shadow-sm border-[1px] border-cardborder p-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Person Name
                      </label>
                      <input
                        type="text"
                        id="storeCoordinator"
                        value={formData.storeCoordinator}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 ${
                          formErrors.storeCoordinator
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-brand'
                        }`}
                      />
                      {formErrors.storeCoordinator && (
                        <p className="mt-1 text-sm text-red-600">
                          {formErrors.storeCoordinator}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Contact Person Number
                      </label>
                      <input
                        type="text"
                        id="phone"
                        value={formData.phone}
                        onChange={(e) => {
                          const numericValue = e.target.value.replace(/[^0-9]/g, '');
                          e.target.value = numericValue;
                          handleChange(e);
                        }}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 ${
                          formErrors.phone
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-brand'
                        }`}
                      />
                      {formErrors.phone && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.phone}</p>
                      )}
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Address
                      </label>
                      <textarea
                        id="address"
                        value={formData.address}
                        onChange={handleChange}
                        rows={3}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 ${
                          formErrors.address
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-brand'
                        }`}
                      />
                      {formErrors.address && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.address}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        State
                      </label>
                      <div className="relative">
                        <select
                          value={selectedState}
                          onChange={(e) => setSelectedState(e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 appearance-none ${
                            formErrors.state
                              ? 'border-red-500 focus:ring-red-500'
                              : 'border-gray-300 focus:ring-brand'
                          }`}
                        >
                          <option value="">Select State</option>
                          {Object.keys(statesAndCities).map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                          <svg
                            className="h-4 w-4 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                      {formErrors.state && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.state}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        City
                      </label>
                      <div className="relative">
                        <select
                          value={city}
                          onChange={(e) => setCity(e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 appearance-none ${
                            formErrors.city
                              ? 'border-red-500 focus:ring-red-500'
                              : 'border-gray-300 focus:ring-brand'
                          }`}
                        >
                          <option value="">Select City</option>
                          {(statesAndCities as Record<string, string[]>)[selectedState]?.map(
                            (c) => (
                              <option key={c} value={c}>
                                {c}
                              </option>
                            ),
                          )}
                        </select>
                        <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                          <svg
                            className="h-4 w-4 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                        </div>
                      </div>
                      {formErrors.city && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.city}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Brand Name
                      </label>
                      <input
                        type="text"
                        id="tenantBrand"
                        value={formData.tenantBrand}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 ${
                          formErrors.tenantBrand
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-brand'
                        }`}
                      />
                      {formErrors.tenantBrand && (
                        <p className="mt-1 text-sm text-red-600">
                          {formErrors.tenantBrand}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Wing
                      </label>
                      <input
                        type="text"
                        id="wing"
                        value={formData.wing}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 ${
                          formErrors.wing
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-brand'
                        }`}
                      />
                      {formErrors.wing && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.wing}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Floor
                      </label>
                      <input
                        type="text"
                        id="floor"
                        value={formData.floor}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-1 ${
                          formErrors.floor
                            ? 'border-red-500 focus:ring-red-500'
                            : 'border-gray-300 focus:ring-brand'
                        }`}
                      />
                      {formErrors.floor && (
                        <p className="mt-1 text-sm text-red-600">{formErrors.floor}</p>
                      )}
                    </div>

                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Upload Tenant Logo
                      </label>
                      {logoPreview && (
                        <div className="relative inline-block mb-4">
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            className="w-24 h-24 object-contain bg-gray-50 rounded-lg border border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={removeLogo}
                            className="absolute -top-2 -right-2 p-1 bg-white rounded-full shadow-md hover:bg-gray-100 transition-colors"
                          >
                            <MdClose className="w-4 h-4 text-gray-600" />
                          </button>
                        </div>
                      )}
                      <input
                        type="file"
                        onChange={handleLogoUpload}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-brand"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {showModal && (
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4">
                <div className="flex items-center justify-between px-6 py-4 border-b">
                  <h3 className="text-lg font-semibold text-gray-900">Capture Image</h3>
                  <button
                    type="button"
                    onClick={closeCameraModal}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  >
                    <MdClose className="w-5 h-5 text-gray-600" />
                  </button>
                </div>
                {modalContent()}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default TenantEditPage;
